import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import DonationQuickAction from '../../components/ui/DonationQuickAction';
import FeedPost from './components/FeedPost';
import CreatePostModal from './components/CreatePostModal';
import ChallengeCard from './components/ChallengeCard';
import UserSuggestions from './components/UserSuggestions';
import TrendingTopics from './components/TrendingTopics';
import FeedFilters from './components/FeedFilters';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const CommunityFeed = () => {
  const [posts, setPosts] = useState([]);
  const [isCreatePostOpen, setIsCreatePostOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({ type: 'all', sort: 'recent' });
  const navigate = useNavigate();

  // Mock data for posts
  const mockPosts = [
    {
      id: 1,
      author: {
        name: "Sarah Chen",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150",
        verified: true
      },
      content: `Just completed my first month with the Clean Water Challenge! 🌊\n\nSeeing the impact of our collective efforts has been incredible. Together, we've helped provide clean water access to over 500 families in rural communities.\n\nEvery drop counts, and every donation makes a difference. Thank you to everyone who joined this journey with me!`,
      donation: {
        amount: 250,
        charity: {
          name: "Water.org",
          logo: "https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=150"
        }
      },
      challenge: {
        name: "Clean Water Challenge",
        progress: 75
      },
      image: "https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=800",
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      likes: 47,
      isLiked: false,
      comments: [
        {
          id: 1,
          author: {
            name: "Michael Rodriguez",
            avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150"
          },
          content: "This is amazing Sarah! Inspired to join the next challenge 💪",
          timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000)
        },
        {
          id: 2,
          author: {
            name: "Emily Johnson",
            avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150"
          },
          content: "The impact photos from the field are so heartwarming. Great work!",
          timestamp: new Date(Date.now() - 30 * 60 * 1000)
        }
      ]
    },
    {
      id: 2,
      author: {
        name: "David Park",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150",
        verified: false
      },
      content: `Proud to support education initiatives in underserved communities today! 📚\n\nJust donated to three different schools to help fund their library programs. Reading opens up worlds of possibilities for these kids.\n\n#EducationForAll #MakeADifference`,
      donation: {
        amount: 500,
        charity: {
          name: "DonorsChoose",
          logo: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=150"
        }
      },
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
      likes: 32,
      isLiked: true,
      comments: [
        {
          id: 3,
          author: {
            name: "Lisa Wang",
            avatar: "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=150"
          },
          content: "Education is such a powerful tool for change. Thank you for your generosity!",
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000)
        }
      ]
    },
    {
      id: 3,
      author: {
        name: "Maria Gonzalez",
        avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150",
        verified: true
      },
      content: `Volunteered at the local food bank this weekend and it was such a humbling experience. 🍽️\n\nMet so many families who are working hard but still struggling to put food on the table. It reminded me how important our community support is.\n\nIf you're looking for ways to get involved locally, I highly recommend reaching out to your neighborhood food bank. They always need volunteers and donations.`,
      image: "https://images.unsplash.com/photo-1593113598332-cd288d649433?w=800",
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
      likes: 28,
      isLiked: false,
      comments: []
    },
    {
      id: 4,
      author: {
        name: "James Thompson",
        avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150",
        verified: false
      },
      content: `Disaster relief update: Our emergency response fund has reached 80% of its goal! 🚨\n\nThe recent natural disasters have affected thousands of families, and your contributions are making a real difference in providing immediate relief - food, shelter, and medical aid.\n\nLet's push together to reach 100% and help even more families in need.`,
      donation: {
        amount: 1000,
        charity: {
          name: "American Red Cross",
          logo: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=150"
        }
      },
      timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000),
      likes: 65,
      isLiked: false,
      comments: [
        {
          id: 4,
          author: {
            name: "Anna Kim",
            avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150"
          },
          content: "Just donated $200. Every bit helps! 🙏",
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000)
        },
        {
          id: 5,
          author: {
            name: "Robert Chen",
            avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150"
          },
          content: "The response from our community has been incredible. Proud to be part of this!",
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000)
        }
      ]
    }
  ];

  // Mock data for featured challenges
  const featuredChallenges = [
    {
      id: 1,
      title: "Holiday Giving Challenge",
      category: "Seasonal Campaign",
      description: "Spread joy this holiday season by supporting families in need. Every donation helps provide warm meals, gifts, and essential supplies to make the holidays brighter for everyone.",
      progress: 67,
      goal: 50000,
      raised: 33500,
      participants: 1247,
      endDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
      featured: true,
      isParticipating: false,
      topContributors: [
        {
          id: 1,
          name: "Sarah Chen",
          avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150"
        },
        {
          id: 2,
          name: "Michael Rodriguez",
          avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150"
        },
        {
          id: 3,
          name: "Emily Johnson",
          avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150"
        }
      ]
    },
    {
      id: 2,
      title: "Education Emergency Fund",
      category: "Education",
      description: "Help students continue their education despite financial hardships. Support scholarships, school supplies, and technology access for underprivileged students.",
      progress: 43,
      goal: 25000,
      raised: 10750,
      participants: 892,
      endDate: new Date(Date.now() + 22 * 24 * 60 * 60 * 1000),
      featured: false,
      isParticipating: true,
      topContributors: [
        {
          id: 4,
          name: "David Park",
          avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150"
        },
        {
          id: 5,
          name: "Lisa Wang",
          avatar: "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=150"
        }
      ]
    }
  ];

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setPosts(mockPosts);
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleCreatePost = (newPost) => {
    const post = {
      id: posts?.length + 1,
      author: {
        name: "John Doe",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150",
        verified: false
      },
      ...newPost,
      likes: 0,
      isLiked: false,
      comments: []
    };
    
    setPosts([post, ...posts]);
  };

  const handleFilterChange = (type, value) => {
    if (type === 'reset') {
      setFilters({ type: 'all', sort: 'recent' });
    } else {
      setFilters(prev => ({ ...prev, [type]: value }));
    }
  };

  const filteredPosts = posts?.filter(post => {
    if (filters?.type === 'all') return true;
    if (filters?.type === 'donations') return post?.donation;
    if (filters?.type === 'stories') return !post?.donation && !post?.challenge;
    if (filters?.type === 'challenges') return post?.challenge;
    if (filters?.type === 'following') return post?.author?.verified; // Mock following logic
    return true;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Sidebar - Hidden on mobile */}
          <div className="hidden lg:block space-y-6">
            <UserSuggestions />
            <TrendingTopics />
          </div>

          {/* Main Feed */}
          <div className="lg:col-span-2 space-y-6">
            {/* Create Post Button */}
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-lg font-medium">
                  JD
                </div>
                <button
                  onClick={() => setIsCreatePostOpen(true)}
                  className="flex-1 text-left px-4 py-3 bg-muted rounded-full text-muted-foreground hover:bg-muted/80 transition-micro"
                >
                  Share your giving story...
                </button>
                <Button
                  onClick={() => setIsCreatePostOpen(true)}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Post
                </Button>
              </div>
            </div>

            {/* Feed Filters */}
            <FeedFilters
              onFilterChange={handleFilterChange}
              activeFilters={filters}
            />

            {/* Posts Feed */}
            <div className="space-y-6">
              {filteredPosts?.length > 0 ? (
                filteredPosts?.map((post) => (
                  <FeedPost key={post?.id} post={post} />
                ))
              ) : (
                <div className="text-center py-12">
                  <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-card-foreground mb-2">
                    No posts found
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your filters or be the first to share something!
                  </p>
                  <Button onClick={() => setIsCreatePostOpen(true)}>
                    Create First Post
                  </Button>
                </div>
              )}
            </div>

            {/* Load More */}
            {filteredPosts?.length > 0 && (
              <div className="text-center py-6">
                <Button variant="outline" onClick={() => console.log('Load more posts')}>
                  Load More Posts
                </Button>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Featured Challenges */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-card-foreground">Featured Challenges</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/giving-challenges')}
                >
                  View All
                </Button>
              </div>
              <div className="space-y-4">
                {featuredChallenges?.slice(0, 2)?.map((challenge) => (
                  <ChallengeCard key={challenge?.id} challenge={challenge} />
                ))}
              </div>
            </div>

            {/* Mobile Sidebar Content */}
            <div className="lg:hidden space-y-6">
              <UserSuggestions />
              <TrendingTopics />
            </div>
          </div>
        </div>
      </div>
      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={isCreatePostOpen}
        onClose={() => setIsCreatePostOpen(false)}
        onSubmit={handleCreatePost}
      />
      {/* Donation Quick Action */}
      <DonationQuickAction />
    </div>
  );
};

export default CommunityFeed;